import React from 'react';
import TimetableCell from './TimetableCell.jsx';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// Robust time normalization helper: "09:00:00" -> "09:00", "9:00" -> "09:00"
export const normalizeTimeStr = (t) => {
  if (!t) return '';
  const clean = String(t).trim().replace(/\s+/g, '');
  const parts = clean.split(':');
  if (parts.length >= 2) {
    const h = parts[0].padStart(2, '0');
    const m = parts[1].padStart(2, '0');
    return `${h}:${m}`;
  }
  return clean;
};

// Case-insensitive day lookup
export const getDaySlotsFromSchedule = (scheduleMap, dayName) => {
  if (!scheduleMap || typeof scheduleMap !== 'object') return {};
  if (scheduleMap[dayName]) return scheduleMap[dayName];
  
  const target = String(dayName).toLowerCase().trim();
  for (const k of Object.keys(scheduleMap)) {
    const cleanK = String(k).toLowerCase().trim();
    if (cleanK === target || cleanK.startsWith(target.slice(0, 3))) {
      return scheduleMap[k] || {};
    }
  }
  return {};
};

// Multi-strategy slot finder
export const findSlotDataInDay = (dayObj, periodSlot, slotIndex = 0) => {
  if (!dayObj || typeof dayObj !== 'object') return null;

  const startNorm = normalizeTimeStr(periodSlot.startTime);
  const endNorm = normalizeTimeStr(periodSlot.endTime);
  const rangeKeyNoSpace = `${startNorm}-${endNorm}`;
  const rangeKeyWithSpace = `${startNorm} - ${endNorm}`;
  const rawRangeKey = `${periodSlot.startTime}-${periodSlot.endTime}`;
  const orderKey = String(periodSlot.slotOrder || slotIndex + 1);
  const labelKey = periodSlot.label ? String(periodSlot.label).toLowerCase().trim() : '';

  // 1. Direct key lookups
  if (dayObj[rangeKeyNoSpace]) return dayObj[rangeKeyNoSpace];
  if (dayObj[rangeKeyWithSpace]) return dayObj[rangeKeyWithSpace];
  if (dayObj[rawRangeKey]) return dayObj[rawRangeKey];
  if (dayObj[startNorm]) return dayObj[startNorm];
  if (dayObj[periodSlot.startTime]) return dayObj[periodSlot.startTime];
  if (dayObj[orderKey]) return dayObj[orderKey];
  if (dayObj[periodSlot.slotOrder]) return dayObj[periodSlot.slotOrder];
  if (dayObj[slotIndex]) return dayObj[slotIndex];
  if (dayObj[`slot_${orderKey}`]) return dayObj[`slot_${orderKey}`];
  if (dayObj[`slot${orderKey}`]) return dayObj[`slot${orderKey}`];

  // 2. Iterate through dayObj entries to find match
  for (const [key, val] of Object.entries(dayObj)) {
    if (!val || typeof val !== 'object') continue;

    // Match by key normalization
    const cleanKey = String(key).replace(/\s+/g, '').toLowerCase();
    if (cleanKey === rangeKeyNoSpace.toLowerCase() || cleanKey === startNorm.toLowerCase() || (labelKey && cleanKey === labelKey)) {
      return val;
    }

    // Match by slot's own properties
    if (val.startTime) {
      const valStartNorm = normalizeTimeStr(val.startTime);
      if (valStartNorm === startNorm) {
        if (!val.endTime || !endNorm || normalizeTimeStr(val.endTime) === endNorm) {
          return val;
        }
      }
    }

    if (val.slotOrder && String(val.slotOrder) === orderKey) {
      return val;
    }

    if (val.label && String(val.label).toLowerCase().trim() === labelKey) {
      return val;
    }
  }

  return null;
};

const TimetableGrid = ({ 
  bellScheduleSlots = [], 
  scheduleMap = {}, 
  onCellClick,
  readOnly = false,
  academicYear,
  timetableId
}) => {
  const slots = (bellScheduleSlots && bellScheduleSlots.length > 0) ? bellScheduleSlots : [
    { slotOrder: 1, label: 'Period 1', startTime: '09:00', endTime: '09:45', slotType: 'TEACHING' },
    { slotOrder: 2, label: 'Period 2', startTime: '09:45', endTime: '10:30', slotType: 'TEACHING' },
    { slotOrder: 3, label: 'Short Break', startTime: '10:30', endTime: '10:45', slotType: 'SHORT_BREAK' },
    { slotOrder: 4, label: 'Period 3', startTime: '10:45', endTime: '11:30', slotType: 'TEACHING' },
    { slotOrder: 5, label: 'Period 4', startTime: '11:30', endTime: '12:15', slotType: 'TEACHING' },
    { slotOrder: 6, label: 'Lunch Break', startTime: '12:15', endTime: '13:00', slotType: 'LUNCH' },
    { slotOrder: 7, label: 'Period 5', startTime: '13:00', endTime: '13:45', slotType: 'TEACHING' },
    { slotOrder: 8, label: 'Period 6', startTime: '13:45', endTime: '14:30', slotType: 'TEACHING' },
    { slotOrder: 9, label: 'Period 7', startTime: '14:30', endTime: '15:15', slotType: 'TEACHING' },
    { slotOrder: 10, label: 'Period 8', startTime: '15:15', endTime: '16:00', slotType: 'TEACHING' },
  ];

  return (
    <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', marginBottom: '24px' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '880px' }}>
          <thead>
            <tr>
              <th style={{ padding: '14px 16px', fontSize: '12.5px', fontWeight: '700', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #E2E8F0', backgroundColor: '#F8FAFC', width: '130px' }}>
                TIME / PERIOD
              </th>
              {DAYS.map(day => (
                <th key={day} style={{ padding: '14px 16px', fontSize: '12.5px', fontWeight: '700', color: '#0F172A', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #E2E8F0', backgroundColor: '#F8FAFC', textAlign: 'center' }}>
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {slots.map((periodSlot, pIdx) => {
              const isBreak = periodSlot.slotType === 'SHORT_BREAK' || periodSlot.slotType === 'LUNCH';
              const startClean = normalizeTimeStr(periodSlot.startTime);
              const endClean = normalizeTimeStr(periodSlot.endTime);
              const rangeKey = `${startClean}-${endClean}`;

              return (
                <tr key={pIdx} style={{ borderBottom: '1px solid #F1F5F9' }}>
                  {/* TIME SLOT HEADER */}
                  <td style={{ padding: '12px 14px', backgroundColor: '#F8FAFC', borderRight: '1px solid #E2E8F0', verticalAlign: 'middle' }}>
                    <div style={{ fontSize: '13px', fontWeight: '700', color: '#0F172A' }}>{periodSlot.label}</div>
                    <div style={{ fontSize: '11.5px', color: '#64748B', marginTop: '2px', fontFamily: 'monospace' }}>
                      {startClean || periodSlot.startTime} – {endClean || periodSlot.endTime}
                    </div>
                  </td>

                  {/* DAY CELLS */}
                  {DAYS.map(day => {
                    const dayObj = getDaySlotsFromSchedule(scheduleMap, day);
                    const slotData = findSlotDataInDay(dayObj, periodSlot, pIdx);

                    return (
                      <td key={day} style={{ padding: '8px', verticalAlign: 'stretch', width: `${100 / 6}%` }}>
                        <TimetableCell
                          slot={slotData}
                          slotType={periodSlot.slotType}
                          readOnly={readOnly}
                          onClick={() => {
                            if (!isBreak && onCellClick) {
                              onCellClick({
                                dayOfWeek: day,
                                timeKey: rangeKey,
                                slotLabel: periodSlot.label,
                                startTime: startClean || periodSlot.startTime,
                                endTime: endClean || periodSlot.endTime,
                                bellScheduleSlotId: periodSlot.id || null,
                                currentSlot: slotData,
                                timetableId,
                                academicYear
                              });
                            }
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <style>{`
        .hover-cell:hover {
          border-color: #2563EB !important;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.15) !important;
          transform: translateY(-1px);
        }
        .hover-empty-cell:hover {
          border-color: #2563EB !important;
          background-color: #EFF6FF !important;
        }
      `}</style>
    </div>
  );
};

export default TimetableGrid;
